import { emit } from '@zapier/events-toolkit-browser';

export enum TrackingEvent {
  UserInteraction = 'web_analytics.tracking.user_interaction.UserInteractionEvent',
  PageView = 'web_analytics.tracking.page_view.PageViewEvent',
}

export enum EventAction {
  click = 'click',
  in_view = 'in_view',
}

export enum InteractionName {
  call_to_action = 'call_to_action',
  card_click = 'card_click',
  component_scrolled_into_view = 'component_scrolled_into_view',
  navigation = 'navigation',
  pill_click = 'pill_click',
  toggle = 'toggle',
}

export enum OptionSelected {
  article_card = 'article_card',
  card = 'card',
  cta_button = 'cta_button',
  cta_link = 'cta_link',
  not_applicable = 'not_applicable',
  pill_link = 'pill_link',
  secondary_cta_button = 'secondary_cta_button',
  sub_nav_link = 'sub_nav_link',
  opening = 'opening',
  closing = 'closing',
}

export enum TrackedComponent {
  ArticleCard = 'ArticleCard',
  AutomateWithZapier = 'AutomateWithZapier',
  FeaturedArticleHeroCard = 'FeaturedArticleHeroCard',
  FullWidthCTA = 'FullWidthCTA',
  FullWidthTextCTA = 'FullWidthTextCTA',
  GenericCTA = 'GenericCTA',
  HalfWidthCTA = 'HalfWidthCTA',
  Hero = 'Hero',
  MiniArticleBlocks = 'MiniArticleBlocks',
  MiniArticleCards = 'MiniArticleCards',
  SignupPopup = 'SignupPopup',
  SubscribeCard = 'SubscribeCard',
  SubscribeMain = 'SubscribeMain',
  SubscribeMini = 'SubscribeMini',
  SubscribePopup = 'SubscribePopup',
  UpNext = 'UpNext',
  ZapierProductModule = 'ZapierProductModule',
}

interface UserInteractionEvent {
  custom_tracking_fields?: Record<string, any>;
  destination_url_address?: string;
  event_action: EventAction;
  interaction_name: InteractionName;
  interaction_raw_text?: string;
  option_selected: string;
}

type CTAClickEvent = Omit<
  UserInteractionEvent,
  'option_selected' | 'event_action' | 'interaction_name'
> & {
  option_selected?:
    | OptionSelected.cta_button
    | OptionSelected.cta_link
    | OptionSelected.secondary_cta_button
    | OptionSelected.opening
    | OptionSelected.closing;
};

type CardClickEvent = Omit<
  UserInteractionEvent,
  'event_action' | 'interaction_name'
>;

type InViewEvent = Omit<
  UserInteractionEvent,
  'event_action' | 'option_selected' | 'interaction_name'
>;

function emitPageViewEvent() {
  return emit({ system_name: 'blog' }, TrackingEvent.PageView);
}

function emitUserInteractionEvent(event: UserInteractionEvent) {
  return emit({ ...event, system_name: 'blog' }, TrackingEvent.UserInteraction);
}

function emitCTAClickEvent(event: CTAClickEvent) {
  emitUserInteractionEvent({
    ...event,
    option_selected: event?.option_selected || OptionSelected.cta_button,
    interaction_name: InteractionName.call_to_action,
    event_action: EventAction.click,
  });
}
function emitToggleClickEvent(event: CTAClickEvent) {
  emitUserInteractionEvent({
    ...event,
    option_selected: event?.option_selected || OptionSelected.cta_button,
    interaction_name: InteractionName.toggle,
    event_action: EventAction.click,
  });
}

function emitCardClickEvent(event: CardClickEvent) {
  emitUserInteractionEvent({
    ...event,
    interaction_name: InteractionName.card_click,
    event_action: EventAction.click,
  });
}

function emitInViewEvent(event: InViewEvent) {
  emitUserInteractionEvent({
    ...event,
    event_action: EventAction.in_view,
    interaction_name: InteractionName.component_scrolled_into_view,
    option_selected: OptionSelected.not_applicable,
  });
}

export function useAvroTracking() {
  return {
    emitCardClickEvent,
    emitCTAClickEvent,
    emitInViewEvent,
    emitPageViewEvent,
    emitUserInteractionEvent,
    emitToggleClickEvent,
  };
}
